import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Sidebar from "../Sidebar";
import RevenueByPolishRegionsHeatmap from "./RevenueByPolishRegionsHeatmap";
import ContractorsComparisonChart from "./ContractorsComparisonChart";
import ContractorActivityTrend from "./ContractorActivityTrend";
import RevenueByCountryHeatmap from "./RevenueByCountryHeatmap";
import RevenueByCitiesHeatmap from "./RevenueByCitiesHeatmap";
import { useLocation } from "react-router-dom";
import { FaBalanceScale } from "react-icons/fa";
import { LayoutDashboard } from "lucide-react";
import { getDB, dbGet, dbPut } from "../../db";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import "./DashboardGrid.css";
import TopBar from "../TopBar";
import { createGlobalStyle } from "styled-components";

// NEW: license-gated DownloadButtons + JWT
import { DownloadButtons } from "../DownloadButtons";
import { jwtDecode } from "jwt-decode";

/* ----------------------- EXPORT MODE (generic) ----------------------- */
const ExportModeStyles = createGlobalStyle`
  .for-export * { pointer-events: none !important; cursor: default !important; }
  .for-export [data-export-hide="true"] { display: none !important; }
`;
const enableExportMode = () => document.body.classList.add("for-export");
const disableExportMode = () => document.body.classList.remove("for-export");
/* -------------------------------------------------------------------- */

const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "20px 20px 40px 20px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  textAlign: "center",
};

const formatPath = (path) => {
  const customNames = {
    "sprzedaz/compare": {
      name: "Sprzedaży / Porównanie sprzedaży",
      icon: <FaBalanceScale />,
    },
  };
  const cleanPath = path.replace(/^\//, "");
  return customNames[cleanPath] || { name: cleanPath.replace("/", " / "), icon: null };
};

const SectionTitle = ({ title, inGrid = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 20px 12px 20px",
      margin: inGrid ? "8px 0 12px 0" : "8px 20px 12px 20px",
      fontSize: "1.05rem",
      fontWeight: 700,
      color: "#2b2b2b",
      position: "relative",
      background: "linear-gradient(to right, rgba(135,206,235,0.12), rgba(135,206,235,0.04))",
      border: "1px solid rgba(135,206,235,0.35)",
      borderRadius: "8px",
      ...(inGrid ? { gridColumn: "1 / -1", paddingLeft: "20px", paddingRight: "20px" } : {}),
    }}
  >
    <LayoutDashboard size={20} strokeWidth={2} color="#87CEEB" />
    <span style={{ position: "relative", display: "inline-block" }}>
      {title}
      <span
        style={{
          position: "absolute",
          left: 0,
          bottom: -3,
          width: "100%",
          height: 2,
          backgroundColor: "#87CEEB",
          borderRadius: 2,
          opacity: 0.7,
        }}
      />
    </span>
  </div>
);

/* ------------ helpers for clean exports (hide in-card controls) ------------ */
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const hideCardControlsTemporarily = (rootEl) => {
  const controls = Array.from(
    rootEl.querySelectorAll(
      'input, select, button, [data-export-hide="true"], .filters, .control-row, .toolbar'
    )
  );
  controls.forEach((el) => {
    el.__prevDisplay = el.style.display;
    el.style.display = "none";
  });
  return () => {
    controls.forEach((el) => {
      el.style.display = el.__prevDisplay || "";
      delete el.__prevDisplay;
    });
  };
};

const captureElementAsJpeg = async (el) => {
  el.scrollIntoView({ block: "center" });
  const restore = hideCardControlsTemporarily(el);
  await wait(200);
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    ignoreElements: (node) => {
      if (!(node instanceof Element)) return false;
      return node.matches('[data-export-hide="true"]');
    },
  });
  restore();
  const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
  return { dataUrl, width: canvas.width, height: canvas.height };
};
/* -------------------------------------------------------------------------- */

// Extract license from token (supports "NO EXPIRE" via type or expire)
function extractLicenseFromToken(token) {
  if (!token) return null;
  try {
    const d = jwtDecode(token) || {};
    const typeRaw = d.LicenseType || d.licenseType || d.plan || d.Plan || "";
    const expRaw  = d.LicenseExpire ?? d.licenseExpire ?? "";

    const t = String(typeRaw).toUpperCase();
    const e = String(expRaw).toUpperCase();

    const typeHasNoExp = t.includes("NO EXPIRE") || t.includes("NO_EXPIRE");
    const expIsFlag    = e === "NO EXPIRE" || e === "NO_EXPIRE";

    let yearIsFar = false;
    if (!expIsFlag && typeof expRaw === "string" && expRaw) {
      const dt = new Date(expRaw);
      yearIsFar = !isNaN(dt) && dt.getUTCFullYear() >= 2099;
    }

    if (typeHasNoExp || expIsFlag || yearIsFar) return "NO EXPIRE";
    return typeRaw || null;
  } catch {
    return null;
  }
}

const SprzedazCompare = () => {
  const [salesData, setSalesData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [licenseType, setLicenseType] = useState(null); // NEW
  const locationData = useLocation();
  const { name, icon } = formatPath(locationData.pathname);

  // Refs
  const contractorsComparisonRef = useRef(null);
  const contractorActivityRef = useRef(null);
  const revenueRegionsRef = useRef(null);
  const revenueCountriesRef = useRef(null);
  const revenueCitiesRef = useRef(null);

  // Load data
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        await getDB();
        const cached = await dbGet("sales", "allSales");
        if (cached?.data?.length) {
          setSalesData(cached.data);
          return;
        }
        const token = localStorage.getItem("token");
        const res = await axios.get("https://server229256.nazwa.pl/api/sprzedaz", {
          headers: { Authorization: token },
        });
        const data = res.data || [];
        setSalesData(data);
        await dbPut("sales", "allSales", { data, timestamp: Date.now() });
      } catch (e) {
        console.error("fetchSalesData error:", e);
      }
    };

    const fetchClientsData = async () => {
      try {
        await getDB();
        const now = Date.now();
        const cached = await dbGet("clients", "allClients");
        if (cached && now - cached.timestamp < 60 * 60 * 1000) {
          setClientsData(cached.data);
          return;
        }
        const res = await axios.get("https://server229256.nazwa.pl/api/klienci");
        const data = res.data || [];
        setClientsData(data);
        await dbPut("clients", "allClients", { data, timestamp: now });
      } catch (e) {
        console.error("fetchClientsData error:", e);
      }
    };

    fetchSalesData();
    fetchClientsData();
  }, []);

  // Pick up license from user and/or token
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const ltUser =
        user?.licenseType ??
        user?.LicenseType ??
        user?.plan ??
        user?.Plan ?? null;
      if (typeof ltUser === "string" && ltUser) setLicenseType(ltUser);
    } catch {/* ignore */}

    const token = localStorage.getItem("token");
    const ltFromToken = extractLicenseFromToken(token);
    if (ltFromToken) setLicenseType(ltFromToken);
  }, []);

  /* ------------ report page plan ------------ */
  const COMPONENTS_FOR_REPORT = [
    { ref: contractorsComparisonRef, title: "Contractors Comparison Chart", layout: "landscape-2col" },
    { ref: contractorActivityRef,    title: "Contractor Activity Trend",    layout: "landscape-2col" },
    { ref: revenueCitiesRef,   title: "Revenue by Cities — Heatmap",         layout: "landscape" },
    { ref: revenueRegionsRef,  title: "Revenue by Polish Regions — Heatmap", layout: "landscape" },
    { ref: revenueCountriesRef, title: "Revenue by Countries — Heatmap",     layout: "landscape" },
  ];

  /* ------------ export handlers ------------ */
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const reportTitle = "Sales Comparison — Report";

      const P_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
      const L_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
      const pages = [];

      const getPageMetrics = (orient) => {
        const isLandscape = orient === "l" || orient === "l2" || orient === "l3";
        const base = isLandscape
          ? { pageW: 297, pageH: 210, margins: L_MARGINS }
          : { pageW: 210, pageH: 297, margins: P_MARGINS };
        const contentW = base.pageW - base.margins.left - base.margins.right;
        const contentH = base.pageH - base.margins.top - base.margins.bottom;
        return { ...base, contentW, contentH };
      };

      let curY = null;
      let l3colCountOnCurrentPage = 0;
      let l2colCountOnCurrentPage = 0;

      const addBlock = (img, title, layout) => {
        const gutter = 6;

        if (layout === "landscape-3col") {
          if (!pages.length || pages[pages.length - 1].orient !== "l3" || l3colCountOnCurrentPage >= 3) {
            pages.push({ orient: "l3", elements: [] });
            l3colCountOnCurrentPage = 0;
          }
          const { margins, contentW, contentH } = getPageMetrics("l");
          const columns = 3;
          const cellW = (contentW - gutter * (columns - 1)) / columns;
          const cellH = contentH * 0.9;
          const col = l3colCountOnCurrentPage;
          const x = margins.left + col * (cellW + gutter);
          const y = margins.top;
          const ratio = img.height / img.width;
          let w = cellW;
          let h = w * ratio;
          if (h > cellH) { h = cellH; w = h / ratio; }
          pages[pages.length - 1].elements.push({ x, y: y + (cellH - h) / 2, w, h, title, dataUrl: img.dataUrl });
          l3colCountOnCurrentPage += 1;
          return;
        }

        if (layout === "landscape-2col") {
          if (!pages.length || pages[pages.length - 1].orient !== "l2" || l2colCountOnCurrentPage >= 2) {
            pages.push({ orient: "l2", elements: [] });
            l2colCountOnCurrentPage = 0;
          }
          const { margins, contentW, contentH } = getPageMetrics("l");
          const columns = 2;
          const cellW = (contentW - gutter * (columns - 1)) / columns;
          const cellH = contentH * 0.9;
          const col = l2colCountOnCurrentPage;
          const x = margins.left + col * (cellW + gutter);
          const y = margins.top;
          const ratio = img.height / img.width;
          let w = cellW;
          let h = w * ratio;
          if (h > cellH) { h = cellH; w = h / ratio; }
          pages[pages.length - 1].elements.push({ x, y: y + (cellH - h) / 2, w, h, title, dataUrl: img.dataUrl });
          l2colCountOnCurrentPage += 1;
          return;
        }

        if (layout === "landscape") {
          pages.push({ orient: "l", elements: [] });
          const { margins, contentW, contentH } = getPageMetrics("l");
          const ratio = img.height / img.width;
          const targetW = contentW;
          const targetH = Math.min(targetW * ratio, contentH * 0.9);
          const startY = margins.top + (contentH - targetH) / 2;
          pages[pages.length - 1].elements.push({
            x: margins.left, y: startY, w: targetW, h: targetH, title, dataUrl: img.dataUrl
          });
          return;
        }

        // portrait fallback
        const { margins, contentW, contentH } = getPageMetrics("p");
        const targetW = contentW;
        const ratio = img.height / img.width;
        const targetH = Math.min(targetW * ratio, contentH * 0.9);
        if (!pages.length || pages[pages.length - 1].orient !== "p") {
          pages.push({ orient: "p", elements: [] });
          const { margins: m } = getPageMetrics("p");
          curY = m.top;
        }
        if (curY + targetH > margins.top + contentH) {
          pages.push({ orient: "p", elements: [] });
          curY = margins.top;
        }
        pages[pages.length - 1].elements.push({
          x: margins.left, y: curY, w: targetW, h: targetH, title, dataUrl: img.dataUrl
        });
        curY += targetH + 6;
      };

      // Capture & place
      for (const block of COMPONENTS_FOR_REPORT) {
        if (!block.ref?.current) continue;
        const img = await captureElementAsJpeg(block.ref.current);
        addBlock(img, block.title, block.layout);
      }

      if (pages.length === 0) pages.push({ orient: "p", elements: [] });

      const firstOrient = pages[0].orient === "p" ? "portrait" : "landscape";
      const pdf = new jsPDF({ orientation: firstOrient, unit: "mm", format: "a4", compress: true });

      const drawHeaderFooter = (pdf, page, pageCount, opts) => {
        const { leftMargin, topMargin, rightMargin, bottomMargin, title } = opts;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(title, leftMargin, topMargin - 5, { baseline: "bottom" });
        pdf.setLineWidth(0.2);
        pdf.line(leftMargin, topMargin - 3, pdf.internal.pageSize.getWidth() - rightMargin, topMargin - 3);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const pageLabel = `Page ${page} / ${pageCount}`;
        const dateLabel = new Date().toISOString().slice(0, 19).replace("T", " ");
        const y = pdf.internal.pageSize.getHeight() - bottomMargin + 5;
        pdf.text(pageLabel, leftMargin, y);
        const w = pdf.getTextWidth(dateLabel);
        pdf.text(dateLabel, pdf.internal.pageSize.getWidth() - rightMargin - w, y);
      };

      const pageCount = pages.length;
      pages.forEach((pg, i) => {
        if (i > 0) {
          const ori = pg.orient === "p" ? "portrait" : "landscape";
          pdf.addPage("a4", ori);
        }
        const { margins } = getPageMetrics(pg.orient);
        drawHeaderFooter(pdf, i + 1, pageCount, {
          leftMargin: margins.left,
          rightMargin: margins.right,
          topMargin: margins.top,
          bottomMargin: margins.bottom,
          title: reportTitle,
        });
        pg.elements.forEach((el) => {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10);
          const titleY = el.y - 2;
          if (titleY > margins.top) pdf.text(el.title, el.x, titleY);
          pdf.addImage(el.dataUrl, "JPEG", el.x, el.y, el.w, el.h, undefined, "FAST");
        });
      });

      pdf.save(`Sprzedaz_porownanie_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PDF.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  const handleDownloadPNG = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const zip = new JSZip();
      for (const c of COMPONENTS_FOR_REPORT) {
        if (!c.ref?.current) continue;
        c.ref.current.scrollIntoView({ block: "center" });
        const restore = hideCardControlsTemporarily(c.ref.current);
        await wait(200);
        const canvas = await html2canvas(c.ref.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          ignoreElements: (node) => {
            if (!(node instanceof Element)) return false;
            return node.matches('[data-export-hide="true"]');
          },
        });
        restore();
        const imgData = canvas.toDataURL("image/png");
        zip.file(`${c.title.replace(/\s+/g, "_")}.png`, imgData.split("base64,")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Sprzedaz_porownanie_${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PNG.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  return (
    <>
      <ExportModeStyles />

      <div style={{ display: "flex", height: "100vh", flexDirection: "row" }}>
        <Sidebar />

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Top bar inside the right/content column */}
          <TopBar />

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 20px",
              fontSize: "1.2rem",
              fontWeight: "bold",
              color: "#87CEEB",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {icon} {name}
            </div>

            {/* License-gated buttons (tooltip-only message + red dashed wrapper when blocked) */}
            <DownloadButtons
              licenseType={licenseType}
              onDownloadPDF={handleDownloadPDF}
              onDownloadPNG={handleDownloadPNG}
              disabled={isDownloading}
            />
          </div>

          {/* Subheader line (kept for layout spacing) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              marginTop: "2px",
              color: "#444",
              fontSize: "1rem",
              padding: "5px 20px 10px 20px",
              maxWidth: "90%",
              lineHeight: 1.5,
            }}
          />

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "20px",
              backgroundColor: "#f5f5f5",
            }}
          >
            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <SectionTitle
                inGrid
                title="Porównanie sprzedaży, średniej wartości transakcji i ich ilości dla wybranych kontrahentów, województw i krajów"
              />
              <div className="card-span-6" style={cardStyle} ref={contractorsComparisonRef}>
                <ContractorsComparisonChart data={salesData} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={contractorActivityRef}>
                <ContractorActivityTrend data={salesData} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={revenueCitiesRef}>
                <RevenueByCitiesHeatmap salesData={salesData} clientsData={clientsData} />
              </div>

              <div className="card-span-12" style={{ ...cardStyle, minHeight: "700px" }} ref={revenueRegionsRef}>
                <RevenueByPolishRegionsHeatmap salesData={salesData} clientsData={clientsData} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={revenueCountriesRef}>
                <RevenueByCountryHeatmap salesData={salesData} clientsData={clientsData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDownloading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#333",
            zIndex: 9999,
          }}
        >
          Poczekaj, trwa generowanie raportu... ⏳
        </div>
      )}
    </>
  );
};

export default SprzedazCompare;
